import * as React from "react";
import { Link } from "react-router-dom";

import { cn } from "@/lib/utils";
import { extractInitials, URLS } from "@/utils";
import { Avatar } from "./avatar";
import { EmailLink, PhoneLink } from "./copyable-link";

type ContactCellSize = "default" | "sm" | "lg" | "xl";

const nameSizeClassName: Record<ContactCellSize, string> = {
  sm: "text-xs",
  default: "text-sm",
  lg: "text-base",
  xl: "text-lg",
};

const fallbackSizeClassName: Record<ContactCellSize, string> = {
  sm: "text-[10px]",
  default: "text-xs",
  lg: "text-sm",
  xl: "text-base",
};

interface ContactCellProps extends Omit<React.ComponentProps<"div">, "onClick"> {
  name?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
  to?: string;
  onClick?: () => void;
  showAvatar?: boolean;
  imageUrl?: string | null;
  avatarBadge?: React.ReactNode;
  avatarClassName?: string;
  size?: ContactCellSize;
  label?: React.ReactNode;
}

function ContactCell({
  name,
  email,
  phoneNumber,
  to,
  onClick,
  showAvatar = false,
  imageUrl,
  avatarBadge,
  avatarClassName,
  size = "default",
  label,
  className,
  children,
  ...props
}: ContactCellProps) {
  const hasEmail = Boolean(email);
  const hasPhone = Boolean(phoneNumber);
  const showDetails = hasEmail || hasPhone;
  const isClickable = Boolean(to || onClick);

  const nameClassName = cn(
    "truncate font-sans font-semibold text-foreground transition-none",
    isClickable && "hover:underline hover:text-primary select-none",
    nameSizeClassName[size]
  );

  let nameNode: React.ReactNode;
  if (to) {
    nameNode = (
      <Link className={nameClassName} to={to}>
        {name}
      </Link>
    );
  } else if (onClick) {
    nameNode = (
      <button
        type="button"
        className={cn(nameClassName, "cursor-pointer appearance-none border-0 bg-transparent p-0 text-left")}
        onClick={onClick}
      >
        {name}
      </button>
    );
  } else {
    nameNode = <div className={nameClassName}>{name}</div>;
  }

  const content = (
    <>
      {label != null && label !== "" && (
        <div className="mb-0.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      )}
      {nameNode}
      {children}
      {showDetails && (
        <div className="flex min-w-0 flex-col">
          {hasEmail && <EmailLink email={email!} />}
          {hasPhone && <PhoneLink phoneNumber={phoneNumber!} />}
        </div>
      )}
    </>
  );

  const avatar = (
    <Avatar size={size}>
      {imageUrl ? <Avatar.Image src={imageUrl} alt={name ?? ""} /> : null}
      <Avatar.Fallback
        className={cn(
          "font-bold uppercase",
          fallbackSizeClassName[size],
          avatarClassName ?? "bg-primary/10 text-primary"
        )}
      >
        {extractInitials(name)}
      </Avatar.Fallback>
    </Avatar>
  );

  let avatarNode: React.ReactNode = avatar;
  if (to) {
    avatarNode = (
      <Link to={to} className="rounded-full" aria-label={name ?? undefined}>
        {avatar}
      </Link>
    );
  } else if (onClick) {
    avatarNode = (
      <button
        type="button"
        className="cursor-pointer appearance-none rounded-full border-0 bg-transparent p-0"
        onClick={onClick}
        aria-label={name ?? undefined}
      >
        {avatar}
      </button>
    );
  }

  if (!showAvatar) {
    return (
      <div className={cn("flex min-w-0 flex-col items-start overflow-hidden", className)} {...props}>
        {content}
      </div>
    );
  }

  return (
    <div className={cn("flex min-w-0 items-start gap-3 overflow-hidden", className)} {...props}>
      <div className="relative shrink-0">
        {avatarNode}
        {avatarBadge != null && (
          <div className="absolute -bottom-1 -right-1 flex items-center justify-center rounded-full bg-white p-0.5 shadow-sm ring-1 ring-slate-100">
            {avatarBadge}
          </div>
        )}
      </div>
      <div className="flex min-w-0 flex-col items-start overflow-hidden">{content}</div>
    </div>
  );
}

interface PlayerCellProps extends Omit<ContactCellProps, "name" | "email" | "to" | "onClick" | "imageUrl"> {
  player: {
    id: number | string;
    fullName?: string | null;
    email?: string | null;
    photoUrl?: string | null;
  };
}

function PlayerCell({ player, className, ...props }: PlayerCellProps) {
  return (
    <ContactCell
      name={player.fullName}
      email={player.email}
      imageUrl={player.photoUrl}
      to={URLS.coach.editPlayer.replace(":playerId", `${player.id}`)}
      className={className}
      {...props}
    />
  );
}

ContactCell.displayName = "ContactCell";
PlayerCell.displayName = "PlayerCell";

export { ContactCell, PlayerCell };
